import { action, computed, observable, toJS, makeAutoObservable } from 'mobx';
import {
    MultiplesColumnIdType,
    LiabilitiesMultiplesResponseType,
    MultiplesSelectionsType,
    FetchStateType,
    LiabilitiesSinglesResponseType,
    SinglesVisibleColumnIdsType,
    SinglesColumnIdType,
    SinglesSelectionsType,
} from './LiabilitiesWidgetTable.types';
import { MultiplesColumns, SinglesColumns } from './LiabilitiesWidgetTable.consts';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { MobxValue } from 'src_common/common/mobx-utils/MobxValue';
import { sortBy } from 'lodash/fp';
import { TypeId } from 'src_common/common/websocket2/type';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { TrpcMultiplesLiabilitiesResponseType, TrpcSinglesLiabilitiesResponseType } from 'src_server/types/liabilities';

function mapData(data: Record<string, unknown>): Record<string, unknown> {
    const elementsToTransform = [
        'stakes',
        'placeStakes',
        'placePayout',
        'totalCashOutBets',
        'totalCashOutStakes',
        'totalCashOutPayout',
        'payout',
        'profit',
        'totalStakes',
        'openStakes',
        'expPayout',
        'netPayout',
        'rumStakes',
        'rumPayout',
    ];

    for (const element of elementsToTransform) {
        const value = data[element];
        if (value !== undefined && (typeof value === 'string' || typeof value === 'number')) {
            data[element] = $appState.configState.precision.newFromAnything(value).value;
        } else if (value !== undefined) {
            console.warn('Wrong data format in Liabilities map data to Amount: value is', value);
        }
    }

    return data;
}

function loadSelectionsNames(
    eventId: number,
    marketId: number,
    liabilities: TrpcSinglesLiabilitiesResponseType | TrpcMultiplesLiabilitiesResponseType
): TrpcSinglesLiabilitiesResponseType['selections'] | TrpcMultiplesLiabilitiesResponseType['selections'] {
    console.warn('Load selections names from market');
    const market = $appState.sdkStaff.models.getMarket(TypeId.newMarketId(eventId, marketId));
    const marketSelections = market?.selections;
    if (marketSelections !== undefined) {
        //@ts-expect-error just for first part of new trading
        return liabilities.selections.map((selection) => {
            const id = selection.id;
            if (id !== null && id !== undefined) {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                marketSelections.find((sel) => {
                    if (sel.id2.rawId === id) {
                        selection.name = sel.name;
                    }
                });
            }
            return selection;
        });
    }
    console.error('No selections in market');
    return [];
}

export class LiabilitiesWidgetTableState {
    private readonly selections: MobxValue<MultiplesSelectionsType | SinglesSelectionsType | string | null>;
    @observable public liabilities: LiabilitiesMultiplesResponseType | LiabilitiesSinglesResponseType | null = null;
    @observable public view: SinglesVisibleColumnIdsType = 'default';
    @observable public direction = 'asc';
    @observable public column: MultiplesColumnIdType | SinglesColumnIdType = 'totalBets';

    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly trpcClient: TrpcClient,
        public marketId: number,
        public eventId: number,
        public singlesTableView?: boolean
    ) {
        if (singlesTableView !== undefined && singlesTableView !== false) {
            this.column = 'profit';
        }

        const selectionsWrapper = {
            connect: (
                _selections: MobxValue<MultiplesSelectionsType | SinglesSelectionsType | string | null>
            ): NodeJS.Timeout => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.fetchLiabilities();

                return setInterval(async () => {
                    await this.fetchLiabilities();
                }, 1000);
            },
            dispose: (timer: NodeJS.Timeout): void => clearInterval(timer),
        };

        this.selections = MobxValue.create({
            initValue: null,
            connect: selectionsWrapper,
        });
        makeAutoObservable(this);
    }

    @action public async fetchLiabilities(): Promise<void> {
        const isMultiples = this.singlesTableView === undefined || this.singlesTableView === false;

        if (isMultiples) {
            try {
                const liabilities = await this.trpcClient.getMultiplesLiabilities({
                    eventId: this.eventId,
                    marketId: this.marketId,
                });
                let withoutName: boolean = false;
                liabilities.selections.forEach((selection) => {
                    if (selection.name === undefined || selection.name === null) {
                        withoutName = true;
                    }
                });
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (withoutName) {
                    // @ts-expect-error
                    liabilities.selections = loadSelectionsNames(this.eventId, this.marketId, liabilities);
                }
                // @ts-expect-error TODO: change types when multiples migrated to socket
                this.liabilitiesFetchSuccess(liabilities);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
                this.liabilitiesFetchFailure(error);
            }
            return;
        }
        try {
            const liabilities = await this.trpcClient.getSinglesLiabilities({
                eventId: this.eventId,
                marketId: this.marketId,
            });
            let withoutName: boolean = false;
            liabilities.selections.forEach((selection) => {
                if (selection.name === undefined || selection.name === null) {
                    withoutName = true;
                }
            });
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (withoutName) {
                // @ts-expect-error
                liabilities.selections = loadSelectionsNames(this.eventId, this.marketId, liabilities);
            }
            // @ts-expect-error TODO: change types when multiples migrated to socket
            this.liabilitiesFetchSuccess(liabilities);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            this.liabilitiesFetchFailure(error);
        }
    }

    @action public liabilitiesFetchSuccess = (liabilities: LiabilitiesMultiplesResponseType): void => {
        liabilities.selections = liabilities.selections.map((selection) => {
            //@ts-expect-error - waiting to BE with precision units
            selection.sgl = mapData(selection.sgl);
            return selection;
        });

        if (this.singlesTableView === true) {
            //@ts-expect-error - waiting to BE with precision units
            liabilities.sgl = mapData(liabilities.sgl);
        }

        this.liabilities = liabilities;
        this.selections.setValue(liabilities.selections);
    };

    @action public liabilitiesFetchFailure = ({ message }: { message: string }): void => {
        this.selections.setValue(message);
    };

    @computed public get state(): FetchStateType {
        const selections = this.selections.getValue();
        if (typeof selections === 'string') {
            return 'ERROR';
        }

        if (selections === null) {
            return 'FETCHING';
        }

        return 'DONE';
    }

    public get allSelections(): MultiplesSelectionsType | SinglesSelectionsType {
        const selections = this.selections.getValue();

        if (selections === null || typeof selections === 'string') {
            return [];
        }

        const comparatorValueMapper =
            this.singlesTableView === undefined || this.singlesTableView === false
                ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  MultiplesColumns[this.column as MultiplesColumnIdType].comparatorValueMapper
                : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  SinglesColumns[this.column as SinglesColumnIdType].comparatorValueMapper;

        const sortedSelections = sortBy(comparatorValueMapper, toJS(selections));

        //@ts-expect-error TODO: check return exact type
        return this.direction === 'desc' ? sortedSelections.reverse() : sortedSelections;
    }

    public get eventName(): string {
        const event = this.sdkStaff.models.getEvent(this.eventId);

        if (event === null) {
            return '';
        }

        return event.name;
    }

    public get marketName(): string {
        const marketId = TypeId.newMarketId(this.eventId, this.marketId);
        const market = this.sdkStaff.models.getMarket(marketId);

        if (market === null) {
            return '';
        }

        return market.name;
    }

    @action public setTableView = (view: SinglesVisibleColumnIdsType): void => {
        this.view = view;
    };

    @action public setSortDirection = (order: string): void => {
        this.direction = order;
    };

    @action public setSortedColumn = (column: MultiplesColumnIdType | SinglesColumnIdType): void => {
        this.column = column;
    };
}
